/**
 * Quality presets (BUILD_PLAN section 4) and the resolution of user settings into the concrete
 * effect configuration the renderer consumes. Pure data and functions; unit-tested.
 */

export const PRESET_NAMES = ['low', 'medium', 'high', 'ultra'] as const;
export type PresetName = (typeof PRESET_NAMES)[number];

export const EFFECT_KEYS = [
  'taa',
  'ao',
  'ssgi',
  'bloom',
  'dof',
  'motionBlur',
  'lut',
  'vignette',
  'chromaticAberration',
  'grain',
] as const;
export type EffectKey = (typeof EFFECT_KEYS)[number];
export type EffectToggles = Record<EffectKey, boolean>;

export const SHADOW_QUALITIES = ['off', 'low', 'medium', 'high'] as const;
export type ShadowQuality = (typeof SHADOW_QUALITIES)[number];

export type FpsCap = 0 | 60 | 120;

export interface PresetDefinition {
  /** Maximum render scale (dynamic resolution may go lower, never higher). */
  renderScale: number;
  shadows: ShadowQuality;
  effects: EffectToggles;
  ao: { scale: number; denoise: boolean };
  ssgi: { sliceCount: number; stepCount: number };
  anisotropy: number;
}

const OFF_EXTRAS = { dof: false, motionBlur: false } as const;

export const PRESETS: Record<PresetName, PresetDefinition> = {
  low: {
    renderScale: 0.67,
    shadows: 'low',
    effects: {
      taa: true,
      ao: false,
      ssgi: false,
      bloom: true,
      lut: true,
      vignette: true,
      chromaticAberration: false,
      grain: false,
      ...OFF_EXTRAS,
    },
    ao: { scale: 0.5, denoise: false },
    ssgi: { sliceCount: 1, stepCount: 12 },
    anisotropy: 2,
  },
  medium: {
    renderScale: 0.85,
    shadows: 'medium',
    effects: {
      taa: true,
      ao: true,
      ssgi: false,
      bloom: true,
      lut: true,
      vignette: true,
      chromaticAberration: true,
      grain: true,
      dof: true,
      motionBlur: false,
    },
    ao: { scale: 0.5, denoise: false },
    ssgi: { sliceCount: 1, stepCount: 12 },
    anisotropy: 4,
  },
  high: {
    renderScale: 1,
    shadows: 'high',
    effects: {
      taa: true,
      ao: true,
      ssgi: false,
      bloom: true,
      lut: true,
      vignette: true,
      chromaticAberration: true,
      grain: true,
      dof: true,
      motionBlur: false,
    },
    ao: { scale: 1, denoise: true },
    ssgi: { sliceCount: 2, stepCount: 8 },
    anisotropy: 8,
  },
  ultra: {
    renderScale: 1,
    shadows: 'high',
    effects: {
      taa: true,
      ao: true,
      ssgi: true,
      bloom: true,
      lut: true,
      vignette: true,
      chromaticAberration: true,
      grain: true,
      dof: true,
      motionBlur: false,
    },
    ao: { scale: 1, denoise: true },
    ssgi: { sliceCount: 2, stepCount: 8 },
    anisotropy: 16,
  },
};

export interface ShadowConfig {
  cascades: number;
  mapSize: number;
  fade: boolean;
  maxFar: number;
}

export const SHADOW_CONFIGS: Record<Exclude<ShadowQuality, 'off'>, ShadowConfig> = {
  low: { cascades: 2, mapSize: 1024, fade: false, maxFar: 80 },
  medium: { cascades: 3, mapSize: 1024, fade: true, maxFar: 120 },
  high: { cascades: 3, mapSize: 2048, fade: true, maxFar: 150 },
};

/** The subset of settings that resolveEffects needs (the full Settings type lives in schema.ts). */
export interface QualityInputs {
  preset: PresetName;
  renderScale: number;
  dynamicResolution: boolean;
  fpsCap: FpsCap;
  shadows: ShadowQuality;
  effects: EffectToggles;
}

export interface EffectConfig {
  preset: PresetName;
  renderScale: number;
  dynamicResolution: boolean;
  fpsCap: FpsCap;
  aa: 'taau' | 'fxaa';
  ao: { scale: number; denoise: boolean } | null;
  ssgi: { sliceCount: number; stepCount: number } | null;
  bloom: { strength: number; radius: number; threshold: number } | null;
  dof: { focusDistance: number; focalLength: number; bokehScale: number } | null;
  motionBlur: { samples: number } | null;
  lut: { intensity: number } | null;
  vignette: { strength: number } | null;
  chromaticAberration: { strength: number } | null;
  grain: { intensity: number } | null;
  shadows: ShadowConfig | null;
  anisotropy: number;
}

export function resolveEffects(s: QualityInputs): EffectConfig {
  const p = PRESETS[s.preset];
  const e = s.effects;
  return {
    preset: s.preset,
    renderScale: s.renderScale,
    dynamicResolution: s.dynamicResolution,
    fpsCap: s.fpsCap,
    aa: e.taa ? 'taau' : 'fxaa',
    ao: e.ao ? { ...p.ao } : null,
    ssgi: e.ssgi ? { ...p.ssgi } : null,
    bloom: e.bloom ? { strength: 0.35, radius: 0.5, threshold: 1.0 } : null,
    dof: e.dof ? { focusDistance: 12, focalLength: 4, bokehScale: 2 } : null,
    motionBlur: e.motionBlur
      ? { samples: s.preset === 'low' || s.preset === 'medium' ? 8 : 16 }
      : null,
    lut: e.lut ? { intensity: 0.6 } : null,
    vignette: e.vignette ? { strength: 0.35 } : null,
    chromaticAberration: e.chromaticAberration ? { strength: 0.12 } : null,
    grain: e.grain ? { intensity: 0.06 } : null,
    shadows: s.shadows === 'off' ? null : { ...SHADOW_CONFIGS[s.shadows] },
    anisotropy: p.anisotropy,
  };
}

/** Which preset the current toggles correspond to, or "custom" when the user diverged. */
export function matchesPreset(s: QualityInputs): PresetName | 'custom' {
  const p = PRESETS[s.preset];
  const same =
    p.shadows === s.shadows &&
    Math.abs(p.renderScale - s.renderScale) < 1e-6 &&
    EFFECT_KEYS.every((k) => p.effects[k] === s.effects[k]);
  return same ? s.preset : 'custom';
}

const GRAPH_KEYS = [
  'aa',
  'ao',
  'ssgi',
  'bloom',
  'dof',
  'motionBlur',
  'lut',
  'vignette',
  'chromaticAberration',
  'grain',
] as const satisfies readonly (keyof EffectConfig)[];

/**
 * True when the post-processing graph must be rebuilt (any effect parameter changed). Render
 * scale, dynamic resolution, fps cap, shadows, and anisotropy are applied in place instead.
 */
export function needsGraphRebuild(a: EffectConfig, b: EffectConfig): boolean {
  return GRAPH_KEYS.some((k) => JSON.stringify(a[k]) !== JSON.stringify(b[k]));
}
